import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { authFetch } from '../services/api';

interface ScreenVisibilityContextType {
  blockedRoutes: Set<string>;
  isRouteBlocked: (route: string) => boolean;
  isSoftura: boolean;
  loading: boolean;
  refreshRestrictions: () => Promise<void>;
}

const ScreenVisibilityContext = createContext<ScreenVisibilityContextType | undefined>(undefined);

export const useScreenVisibility = () => {
  const context = useContext(ScreenVisibilityContext);
  if (context === undefined) {
    throw new Error('useScreenVisibility must be used within a ScreenVisibilityProvider');
  }
  return context;
};

export const ScreenVisibilityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();
  const [blockedRoutes, setBlockedRoutes] = useState<Set<string>>(new Set());
  const [isSoftura, setIsSoftura] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchRestrictions = useCallback(async () => {
    if (!isAuthenticated || !currentUser) {
      setBlockedRoutes(new Set());
      setIsSoftura(false);
      setLoading(false);
      return;
    }

    try {
      const response = await authFetch('/api/screen-visibility/my-restrictions');
      if (response.ok) {
        const data = await response.json();
        setBlockedRoutes(new Set(data.blockedRoutes || []));
        setIsSoftura(data.isSoftura || false);
      }
    } catch (err) {
      console.error('Error fetching screen visibility restrictions:', err);
      // On error, default to no restrictions (fail-open)
      setBlockedRoutes(new Set());
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, currentUser]);

  useEffect(() => {
    fetchRestrictions();
  }, [fetchRestrictions]);

  const isRouteBlocked = useCallback((route: string): boolean => {
    if (isSoftura) return false;
    return blockedRoutes.has(route);
  }, [blockedRoutes, isSoftura]);

  return (
    <ScreenVisibilityContext.Provider value={{
      blockedRoutes,
      isRouteBlocked,
      isSoftura,
      loading,
      refreshRestrictions: fetchRestrictions
    }}>
      {children}
    </ScreenVisibilityContext.Provider>
  );
};
